import { useNavigate } from "react-router-dom";
import { ArrowLeft, Package, User, Phone, Mail, Calendar, CreditCard, Truck, CheckCircle, XCircle, Loader2 } from "lucide-react";
import PageLayout from "../../../layouts/PageLayout";
import useCustomParams from "../../../hooks/params/useCustomParams";
import { useOrder } from "../../../hooks/orders/useOrder";

export default function OrderDetailsPage() {
  const navigate = useNavigate();
  const { id } = useCustomParams();
  const { order, loading } = useOrder({ id: id! });

  if (loading) {
    return (
      <PageLayout title="Détails de la commande">
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin h-12 w-12 text-primary" />
        </div>
      </PageLayout>
    );
  }

  if (!order) {
    return (
      <PageLayout title="Détails de la commande">
        <div className="text-center py-20 text-gray-500">Commande non trouvée</div>
      </PageLayout>
    );
  }

  const getStatusBadge = (status: string) => {
    const statusMap = {
      pending: { label: "En attente", className: "bg-yellow-100 text-yellow-700" },
      approved: { label: "Approuvée", className: "bg-green-100 text-green-700" },
      canceled: { label: "Annulée", className: "bg-red-100 text-red-700" }
    };
    const s = statusMap[status as keyof typeof statusMap] || statusMap.pending;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${s.className}`}>{s.label}</span>;
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <PageLayout
      title="Détails de la commande"
      actions={
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
        >
          <ArrowLeft size={18} /> Retour
        </button>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informations générales */}
        <div className="lg:col-span-2 space-y-6">
          {/* En-tête commande */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold">{order.reference}</h2>
                <p className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                  <Calendar size={14} />
                  {formatDate(order.createdAt)}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {getStatusBadge(order.status)}
                {order.transactionId && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                    Paiement effectué
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Articles */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package size={18} className="text-primary" />
              Articles
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b">
                  <tr className="text-left text-sm text-gray-500">
                    <th className="pb-2">Produit</th>
                    <th className="pb-2">Longueur</th>
                    <th className="pb-2 text-center">Quantité</th>
                    <th className="pb-2 text-right">Prix unitaire</th>
                    <th className="pb-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item, idx) => (
                    <tr key={idx} className="border-b last:border-0">
                      <td className="py-3 font-medium">{item.productName}</td>
                      <td className="py-3">{item.length}</td>
                      <td className="py-3 text-center">{item.quantity}</td>
                      <td className="py-3 text-right">{item.price.toLocaleString('fr-FR')} FCFA</td>
                      <td className="py-3 text-right font-semibold">
                        {(item.price * item.quantity).toLocaleString('fr-FR')} FCFA
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4} className="pt-4 text-right font-semibold text-gray-700">
                      Total
                    </td>
                    <td className="pt-4 text-right font-bold text-lg text-primary">
                      {order.amount.toLocaleString('fr-FR')} FCFA
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Client */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <User size={18} className="text-primary" />
              Client
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <User size={16} className="text-gray-400 mt-0.5" />
                <div>
                  <p className="font-medium">{order.customerName}</p>
                  <p className="text-sm text-gray-500">ID: {order.userId}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={16} className="text-gray-400" />
                <a href={`mailto:${order.customerEmail}`} className="text-sm text-primary hover:underline">
                  {order.customerEmail}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={16} className="text-gray-400" />
                <a href={`tel:${order.customerPhone}`} className="text-sm text-primary hover:underline">
                  {order.customerPhone}
                </a>
              </div>
            </div>
          </div>

          {/* Informations commande */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CreditCard size={18} className="text-primary" />
              Informations commande
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-500">Statut</span>
                {getStatusBadge(order.status)}
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Montant</span>
                <span className="font-bold">{order.amount.toLocaleString('fr-FR')} FCFA</span>
              </div>
              {order.transactionId && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Transaction</span>
                  <span className="font-mono text-sm">{order.transactionId}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">Date commande</span>
                <span className="text-sm">{formatDate(order.createdAt)}</span>
              </div>
              {order.updatedAt && order.updatedAt !== order.createdAt && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Dernière modification</span>
                  <span className="text-sm">{formatDate(order.updatedAt)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Actions rapides */}
          {order.status === "pending" && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Truck size={18} className="text-primary" />
                Actions
              </h3>
              <div className="flex flex-col gap-2">
                <button className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition">
                  <CheckCircle size={18} /> Approuver la commande
                </button>
                <button className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition">
                  <XCircle size={18} /> Annuler la commande
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
}